import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Stack, IconButton, Typography, Button } from '@mui/material'
import { Add, Remove } from '@mui/icons-material'
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton'
import { formatTemperature } from '../utils/string'
import { AuroraSetMaxTemperatureLimitActionInput } from '../types/AuroraAwhpActionInput'
import PinCodeDialog from './PinCodeDialog'
import useDialog from '../hooks/useDialog'
import { AuroraAwhpActionNack } from '../errors'
import AuroraControl from './AuroraControl'
import { AURORA_MAX_TEMPERATURE_LIMIT, AURORA_MIN_TEMPERATURE_LIMIT } from '../constants/aurora'
import { createWarningNotification } from '../redux/slices/notifications'

export interface AuroraMaxTemperatureLimitControlProps {
  value: number | null
  loading?: boolean
  disabled?: boolean
  onSubmit: (input: AuroraSetMaxTemperatureLimitActionInput) => Promise<void>
}

export default function AuroraMaxTemperatureLimitControl({
  value,
  loading = false,
  disabled = false,
  onSubmit
}: AuroraMaxTemperatureLimitControlProps) {
  const { t } = useTranslation('aurora')

  const dispatch = useDispatch()

  const { open, openDialog, closeDialog } = useDialog()

  const [maxTempLimit, setMaxTempLimit] = useState<number | null>(null)

  function clampToHardLimits(value: number) {
    return Math.min(Math.max(value, AURORA_MIN_TEMPERATURE_LIMIT), AURORA_MAX_TEMPERATURE_LIMIT)
  }

  function handleIncrement() {
    const current = maxTempLimit !== null ? maxTempLimit : value

    if (current === null) {
      return
    }

    const nextValue = Math.ceil(current * 2) / 2 + 0.5

    if (nextValue > AURORA_MAX_TEMPERATURE_LIMIT) {
      dispatch(
        createWarningNotification(
          t('maxTemperatureLimitWarning', { value: AURORA_MAX_TEMPERATURE_LIMIT })
        )
      )
    }

    setMaxTempLimit(clampToHardLimits(nextValue))
  }

  function handleDecrement() {
    const current = maxTempLimit !== null ? maxTempLimit : value

    if (current === null) {
      return
    }

    const nextValue = Math.floor(current * 2) / 2 - 0.5

    if (nextValue < AURORA_MIN_TEMPERATURE_LIMIT) {
      dispatch(
        createWarningNotification(
          t('minTemperatureLimitWarning', { value: AURORA_MIN_TEMPERATURE_LIMIT })
        )
      )
    }

    setMaxTempLimit(clampToHardLimits(nextValue))
  }

  async function handleSubmit(pin: number) {
    closeDialog()

    if (maxTempLimit === null) {
      return
    }

    try {
      await onSubmit({
        alarms: {
          pin,
          maxTempLimit
        }
      })

      setMaxTempLimit(null)
    } catch (error) {
      if (error instanceof AuroraAwhpActionNack) {
        switch (error.message) {
          case 'PIN_MISSING':
          case 'PIN_REJECTED':
            openDialog()
            break
        }
      }
    }
  }

  function handleReset() {
    setMaxTempLimit(null)
  }

  return (
    <>
      <AuroraControl
        direction="column"
        label={t('maxTemperatureLimit')}
        loading={loading}
        disabled={disabled}
        control={
          <Stack direction="column" justifyContent="center" alignItems="center" gap={2} pt={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={3}>
              <IconButton
                color={maxTempLimit !== null ? 'primary' : 'inherit'}
                disabled={loading || disabled}
                size="small"
                sx={{ border: '1px solid' }}
                onClick={handleDecrement}
              >
                <Remove />
              </IconButton>

              {maxTempLimit !== null ? (
                <Typography variant="h2" color={loading ? 'text.disabled' : 'primary'}>
                  {formatTemperature(maxTempLimit)}
                </Typography>
              ) : (
                <Typography variant="h2" color="text.primary">
                  {formatTemperature(value)}
                </Typography>
              )}

              <IconButton
                color={maxTempLimit !== null ? 'primary' : 'inherit'}
                disabled={loading || disabled}
                size="small"
                sx={{
                  border: '1px solid'
                }}
                onClick={handleIncrement}
              >
                <Add />
              </IconButton>
            </Stack>

            {maxTempLimit !== null && (
              <Stack direction="row" alignItems="center" gap={2}>
                <LoadingButton
                  variant="outlined"
                  color="primary"
                  size="small"
                  loading={loading}
                  disabled={disabled}
                  onClick={openDialog}
                >
                  {t('actions:confirm')}
                </LoadingButton>

                {!loading && (
                  <Button
                    variant="text"
                    color="inherit"
                    size="small"
                    disabled={disabled}
                    onClick={handleReset}
                  >
                    {t('actions:cancel')}
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        }
      />

      <PinCodeDialog open={open} onClose={closeDialog} onSubmit={handleSubmit} />
    </>
  )
}
